/**
 * The lines a movement is read on — the chart's „Linia" switch, the change table's rows and the
 * session table's figure columns — decided by the COMBINATION of fields the movement measures and
 * never by the movement itself (owner, 24 September 2026). A weighted pull-up measures weight and
 * reps, so it is read like a bench press; a farmer's carry measures weight and distance, so it is
 * read by what was carried and how far.
 *
 * Every line whose fields are measured is offered, and the FIRST one is the backend's primary
 * metric (`LiftMetrics.PrimaryFor`): load with reps, load, distance, reps, time, rounds, speed.
 * RPE, RIR and the tempo field never become a line.
 *
 * A movement whose fields are unknown (a backend older than the field list) keeps the four
 * strength lines it has always had, unfiltered.
 */

import { clockDuration, setDuration } from '~/shared/lib/clockDuration';
import { numberFormat } from '~/shared/lib/intlCache';
import type { Translate } from '~/shared/lib/localeTranslate';
import { signedText } from '~/shared/lib/signedText';
import type { MeasurementFieldKey } from '~/shared/measurement';

import { sessionFigure, type LiftSession } from './liftSessions';

export const LIFT_LINES = [
  'e1rm',
  'weight',
  'reps',
  'volume',
  'bestSet',
  'longestSet',
  'totalTime',
  'time',
  'distance',
  'pace',
  'rounds',
  'speed',
] as const;
export type LiftLine = (typeof LIFT_LINES)[number];

/** What a movement measures; `null` when the read did not say. */
export type LiftFields = readonly MeasurementFieldKey[] | null;

export interface LinePlan {
  /** In the switch's order; the first is the primary metric and the default. */
  readonly lines: readonly LiftLine[];
  /** The session table's figure columns, before Spadek. */
  readonly columns: readonly LiftLine[];
  /** Whether the session table has a „Spadek" column: load with reps only. */
  readonly drop: boolean;
}

const STRENGTH: LinePlan = {
  lines: ['e1rm', 'weight', 'reps', 'volume'],
  columns: ['volume', 'e1rm'],
  drop: true,
};

/** The session table has three figure tracks, Spadek included. */
const FIGURE_COLUMNS = 3;

/** A best-set line whose session sum already has a column of its own. */
const SUMMED_ELSEWHERE: ReadonlySet<LiftLine> = new Set(['bestSet', 'longestSet']);

export function linePlan(fields: LiftFields): LinePlan {
  if (fields === null) return STRENGTH;
  const has = (field: MeasurementFieldKey) => fields.includes(field);
  const strength = has('weight') && has('reps');
  const lines: LiftLine[] = strength ? [...STRENGTH.lines] : [];
  if (!strength && has('weight')) lines.push('weight');
  if (has('distance')) lines.push(...(has('duration') ? (['distance', 'time', 'pace'] as const) : (['distance'] as const)));
  if (!strength && has('reps')) lines.push('bestSet', 'reps');
  if (has('duration') && !has('distance')) lines.push('longestSet', 'totalTime');
  if (has('rounds')) lines.push('rounds');
  if (has('speed')) lines.push('speed');
  if (strength) return { ...STRENGTH, lines };
  return {
    lines,
    columns: lines.filter((line) => !SUMMED_ELSEWHERE.has(line)).slice(0, FIGURE_COLUMNS),
    drop: false,
  };
}

export interface MovementLines extends LinePlan {
  /** The line the chart draws: the one asked for when it is offered, else the first offered one. */
  readonly line: LiftLine | null;
  /**
   * What the fields promise before any session is read, so the switch can stand through the wait:
   * every planned line and the one the chart will start on. Empty while the fields are unknown.
   */
  readonly promised: { readonly lines: readonly LiftLine[]; readonly line: LiftLine | null };
  /** The change table's rows to reserve: one per planned line, never fewer than one for a sentence. */
  readonly reserve: number;
}

/** Fewer than this many sessions with a figure on a line and the line is not offered. */
const SESSIONS_FOR_A_LINE = 2;

/**
 * **The entry point**: what this movement can be read on. A line is offered when the movement
 * measures its fields AND at least two sessions carry a figure on it. With none offered (a single
 * session) the chart still names the combination's first line, so the current reading shows.
 */
export function movementLines(
  fields: LiftFields,
  sessions: readonly LiftSession[],
  requested: LiftLine,
): MovementLines {
  const plan = linePlan(fields);
  const lines =
    fields === null
      ? plan.lines
      : plan.lines.filter(
          (line) =>
            sessions.filter((session) => sessionFigure(session, line) !== null).length >=
            SESSIONS_FOR_A_LINE,
        );
  const planned = plan.lines.includes(requested) ? requested : (plan.lines[0] ?? null);
  return {
    ...plan,
    lines,
    line: lines.includes(requested) ? requested : (lines[0] ?? plan.lines[0] ?? null),
    promised: fields === null ? { lines: [], line: null } : { lines: plan.lines, line: planned },
    reserve: Math.max(1, plan.lines.length),
  };
}

type LineFormat = 'kg' | 'count' | 'clock' | 'distance' | 'pace' | 'speed';

/** Which way is better. A session's total time is neither: a faster run takes less of it. */
export type LineDirection = 'higher' | 'lower' | 'none';

interface LineSpec {
  readonly format: LineFormat;
  readonly better: LineDirection;
  /** The `web:forma.unit.*` key, where the figure carries a unit word. */
  readonly unit: string | null;
}

const SPECS: Readonly<Record<LiftLine, LineSpec>> = {
  e1rm: { format: 'kg', better: 'higher', unit: 'e1rm' },
  weight: { format: 'kg', better: 'higher', unit: 'weight' },
  reps: { format: 'count', better: 'higher', unit: 'reps' },
  volume: { format: 'kg', better: 'higher', unit: 'weight' },
  bestSet: { format: 'count', better: 'higher', unit: 'reps' },
  longestSet: { format: 'clock', better: 'higher', unit: null },
  totalTime: { format: 'clock', better: 'higher', unit: null },
  time: { format: 'clock', better: 'none', unit: null },
  distance: { format: 'distance', better: 'higher', unit: null },
  pace: { format: 'pace', better: 'lower', unit: 'pace' },
  rounds: { format: 'count', better: 'higher', unit: 'rounds' },
  speed: { format: 'speed', better: 'higher', unit: 'speed' },
};

export interface LineWordsDeps {
  readonly t: Translate;
  readonly locale: string;
}

/** How one line's figures are drawn and written. Raw figures are in `sessionFigure`'s units. */
export interface LineWords {
  /** The unit word beside a figure; empty for a time. */
  readonly unit: string;
  /** The unit a table row names when its figures stand bare: km, min/km, km/h. `null` for a lift. */
  readonly rowUnit: string | null;
  /** Raw → the chart's own units: kilometres for a long distance. */
  readonly toChart: (raw: number) => number;
  /** A figure already in the chart's units, bare. What the chart prints: a time as a clock. */
  readonly chartFigure: (value: number) => string;
  /** A raw figure, bare: a set's time as the set rule writes it, „45 s", „2:30". */
  readonly figure: (raw: number) => string;
  readonly withUnit: (raw: number) => string;
  /** A change rounded to the precision `figure` prints, so what is judged is what is read. */
  readonly settle: (delta: number) => number;
  /** „+2,5 kg", „−0:12 /km": the sign is the figure's own, whatever direction is better. */
  readonly signedWithUnit: (delta: number) => string;
  readonly isImprovement: (delta: number) => boolean;
  /** Which end of the chart's axis is better. A line with no direction draws like a higher one. */
  readonly chartBetter: 'higher' | 'lower';
  /** Whether the chart has to print its figures through `chartFigure` rather than as numbers. */
  readonly clock: boolean;
}

const KILOMETRE = 1000;

/** The smallest step each format prints, in the chart's units. */
const PRINTED_STEP: Readonly<Record<LineFormat, number>> = {
  kg: 0.1,
  count: 0.1,
  speed: 0.1,
  clock: 1,
  pace: 1,
  distance: 1,
};
const KILOMETRE_STEP = 0.01;

/**
 * `largest` is the biggest raw figure the words will write, which is what picks metres or
 * kilometres for a distance: a carry reads „30 m", a run „5,2 km", and one series never mixes them.
 */
export function lineWords(line: LiftLine, { t, locale }: LineWordsDeps, largest = 0): LineWords {
  const spec = SPECS[line];
  const one = numberFormat(locale, { maximumFractionDigits: 1 });
  // A session's volume runs into thousands of kilograms, and Polish groups a four-digit figure only
  // when asked: „1 250 kg", not „1250 kg" (owner, 24 September 2026).
  const kg = numberFormat(locale, { maximumFractionDigits: 1, useGrouping: 'always' });
  const kilometres = spec.format === 'distance' && Math.abs(largest) >= KILOMETRE;
  const unit =
    spec.format === 'distance'
      ? t(kilometres ? 'web:forma.unit.km' : 'web:forma.unit.m')
      : spec.unit === null
        ? ''
        : t(`web:forma.unit.${spec.unit}`);
  const scale = kilometres ? KILOMETRE : 1;
  const toChart = (raw: number): number => raw / scale;
  const chartFigure = (value: number): string => {
    switch (spec.format) {
      case 'distance':
        return numberFormat(locale, { maximumFractionDigits: kilometres ? 2 : 0 }).format(value);
      case 'clock':
      case 'pace':
        return clockDuration(value);
      case 'kg':
        return kg.format(value);
      case 'count':
      case 'speed':
        return one.format(value);
    }
  };
  const figure = (raw: number) => (spec.format === 'clock' ? setDuration(raw) : chartFigure(toChart(raw)));
  const withUnit = (raw: number) => (unit === '' ? figure(raw) : `${figure(raw)} ${unit}`);
  const step = kilometres ? KILOMETRE_STEP : PRINTED_STEP[spec.format];
  const settle = (delta: number): number => (Math.round(toChart(delta) / step) * step * scale) || 0;
  return {
    unit,
    rowUnit:
      spec.format === 'distance' || spec.format === 'speed'
        ? unit
        : spec.format === 'pace'
          ? t('web:forma.unit.minPerKm')
          : null,
    toChart,
    chartFigure,
    figure,
    withUnit,
    settle,
    signedWithUnit: (delta) => {
      const settled = settle(delta);
      return signedText(withUnit(Math.abs(settled)), settled);
    },
    isImprovement: (delta) => {
      const settled = settle(delta);
      return spec.better === 'higher' ? settled > 0 : spec.better === 'lower' ? settled < 0 : false;
    },
    chartBetter: spec.better === 'lower' ? 'lower' : 'higher',
    clock: spec.format === 'clock' || spec.format === 'pace',
  };
}

/**
 * The backend's name for the number a movement's four-week change is read on (`LiftMetric`), and
 * the line that writes it. Each is the best single set, which is why reps and time are the best
 * and the longest set rather than the session's sums.
 */
const METRIC_LINES = {
  e1rm: 'e1rm',
  weight: 'weight',
  distance: 'distance',
  reps: 'bestSet',
  duration: 'longestSet',
  rounds: 'rounds',
  speed: 'speed',
} as const satisfies Record<string, LiftLine>;
export type LiftMetric = keyof typeof METRIC_LINES;

/** The wire's metric name (`"E1rm"`, `"Distance"`), or `null` for one this build cannot write. */
export function liftMetric(wire: string | null | undefined): LiftMetric | null {
  const key = wire?.toLowerCase();
  return key !== undefined && Object.hasOwn(METRIC_LINES, key) ? (key as LiftMetric) : null;
}

export function metricLine(metric: LiftMetric): LiftLine {
  return METRIC_LINES[metric];
}

/**
 * One line's words sized to the movement's largest figure on it, so a distance reads in one unit
 * down the whole table and chart.
 */
export function sizedLineWords(sessions: readonly LiftSession[], line: LiftLine, deps: LineWordsDeps): LineWords {
  const largest = sessions.reduce(
    (widest, session) => Math.max(widest, Math.abs(sessionFigure(session, line) ?? 0)),
    0,
  );
  return lineWords(line, deps, largest);
}

/** One session's figure on a line, in the chart's units, at the instant it was finished. */
export interface LineReading {
  readonly at: number;
  readonly value: number;
}

/** A line's readings, session by session, in the chart's units. A session with no figure on it is skipped. */
export function lineReadings(
  sessions: readonly LiftSession[],
  line: LiftLine,
  words: LineWords,
): readonly LineReading[] {
  return sessions.flatMap((session) => {
    const value = sessionFigure(session, line);
    return value === null || !Number.isFinite(value) ? [] : [{ at: session.performedAt, value: words.toChart(value) }];
  });
}
